import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { callContract } from '../contractClient'
import { contracts } from '../../../../shared/features/platform.contracts'
import { contracts as applianceContracts } from '../../../../shared/features/appliance_bootstrap.contracts'
import { contracts as settingsContracts } from '../../../../shared/features/settings.contracts'
import { Button, Card, CardBody, Spinner } from '../ui'
import SetupScreen from './SetupScreen'
import { WORKSPACE_SETUP_REQUIRED_EVENT } from '../services/apiClient'
import { refreshVanityConfig } from '../services/apiClient'
import {
  firstIncompleteSetupIndex,
  setupIndexForPath,
  setupPathForIndex,
} from './setupRoutes'
import { configureMfaProviderIntegration } from './mfaSetupProviders'
import useVisibilityRefresh from '../hooks/useVisibilityRefresh'

function CenteredShell({ children, testId }) {
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-[var(--app-bg)] p-4"
      data-testid={testId}
    >
      {children}
    </div>
  )
}

function setupPollDelayMs(snapshot) {
  const serverHint = snapshot?.setup?.next_poll_ms
  if (Number.isFinite(serverHint)) return Math.min(15000, Math.max(2000, serverHint))
  return 10000
}

function safeReturnPath(value) {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
    ? value
    : '/'
}

/**
 * Workspace setup is resolved before entitlement/timezone/application
 * providers mount. Incomplete workspaces therefore cannot render or prefetch
 * the product shell; the worker remains the authoritative enforcement layer.
 */
export default function SetupGate({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [snapshot, setSnapshot] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const refreshInFlight = useRef(null)

  const refresh = useCallback(async ({ signal, reason } = {}) => {
    if (refreshInFlight.current && !refreshInFlight.current.signal?.aborted) {
      return refreshInFlight.current.promise
    }
    const useBootstrapCache = reason === 'initial'
    const pending = (async () => {
      try {
        const response = await callContract(
          contracts.workspaceSetupState,
          {
            config: {
              ...(!useBootstrapCache ? { sessionBootstrapCache: false } : {}),
              ...(signal && !useBootstrapCache ? { signal } : {}),
            },
          },
        )
        if (signal?.aborted) return
        setSnapshot(response.data)
        setError(false)
      } catch (requestError) {
        if (signal?.aborted) return
        console.error('Failed to load workspace setup state', {
          status: requestError.response?.status || null,
        })
        setError(true)
      } finally {
        if (!signal?.aborted) setLoading(false)
      }
    })()
    refreshInFlight.current = { promise: pending, signal }
    try {
      await pending
    } finally {
      if (refreshInFlight.current?.promise === pending) refreshInFlight.current = null
    }
  }, [])

  const applyTimezone = useCallback(async (timezone) => {
    await callContract(settingsContracts.updateGeneralSettings, { body: { timezone } })
    await refresh()
  }, [refresh])

  const createVanityDomains = useCallback(async (baseDomain) => {
    const existing = snapshot?.setup?.steps
      ?.find((step) => step.key === 'vanity_domains')?.hostnames || []
    const kinds = new Set(existing.map((row) => row.kind))
    try {
      if (!kinds.has('dashboard')) {
        await callContract(contracts.createVanityHostname, {
          body: { kind: 'dashboard', hostname: `dashboard.${baseDomain}` },
        })
      }
      if (!kinds.has('api')) {
        await callContract(contracts.createVanityHostname, {
          body: { kind: 'api', hostname: `api.${baseDomain}` },
        })
      }
      await refreshVanityConfig()
    } finally {
      await refresh()
    }
  }, [refresh, snapshot?.setup?.steps])

  const deleteVanityDomain = useCallback(async (id) => {
    try {
      await callContract(contracts.deleteVanityHostname, { params: { id } })
      await refreshVanityConfig()
    } finally {
      await refresh()
    }
  }, [refresh])

  const verifyMfaProvider = useCallback(async (providerKey) => {
    const response = await callContract(contracts.updateWorkspaceSetupMfa, {
      body: {
        disposition: 'configured',
        provider_key: providerKey,
        expected_revision: snapshot?.setup?.revision,
      },
    })
    setSnapshot(response.data)
  }, [snapshot?.setup?.revision])

  const configureMfaProvider = useCallback(async (providerKey, credentials) => {
    await configureMfaProviderIntegration(providerKey, credentials)
    await verifyMfaProvider(providerKey)
  }, [verifyMfaProvider])

  const skipMfaProvider = useCallback(async () => {
    const response = await callContract(contracts.updateWorkspaceSetupMfa, {
      body: {
        disposition: 'skipped',
        provider_key: null,
        expected_revision: snapshot?.setup?.revision,
      },
    })
    setSnapshot(response.data)
  }, [snapshot?.setup?.revision])

  const requestApplianceDownload = useCallback(async () => {
    const response = await callContract(applianceContracts.requestApplianceDownload, { body: {} })
    return response.data
  }, [])

  const createApplianceEnrollmentCode = useCallback(async (agentName) => {
    const response = await callContract(contracts.createSetupApplianceEnrollmentCode, {
      body: { agent_name: agentName, expires_in_minutes: 15 },
    })
    return response.data
  }, [])

  const startApplianceBootstrap = useCallback(async (agentId) => {
    const response = await callContract(applianceContracts.startBootstrap, {
      body: { agent_id: agentId },
    })
    await refresh()
    return response.data
  }, [refresh])

  const applyAiRunner = useCallback(async (action, target) => {
    const response = await callContract(contracts.updateWorkspaceSetupAiRunner, {
      body: {
        action,
        ...(action === 'install' ? { agent_id: target } : { install_id: target }),
        expected_revision: snapshot?.setup?.revision,
      },
    })
    setSnapshot(response.data)
  }, [snapshot?.setup?.revision])

  const finishSetup = useCallback(async () => {
    const response = await callContract(contracts.finishWorkspaceSetup, {
      body: { expected_revision: snapshot?.setup?.revision },
    })
    setSnapshot(response.data)
  }, [snapshot?.setup?.revision])

  useEffect(() => {
    window.addEventListener(WORKSPACE_SETUP_REQUIRED_EVENT, refresh)
    return () => window.removeEventListener(WORKSPACE_SETUP_REQUIRED_EVENT, refresh)
  }, [refresh])

  useEffect(() => {
    if (!snapshot?.setup) return
    const currentIndex = setupIndexForPath(location.pathname)
    if (snapshot.setup.status === 'complete') {
      if (currentIndex !== null) {
        navigate(safeReturnPath(location.state?.setupReturnTo), { replace: true })
      }
      return
    }

    const firstIncomplete = snapshot.setup.can_manage
      ? firstIncompleteSetupIndex(snapshot.setup.steps || [])
      : 1
    if (currentIndex === null || currentIndex > firstIncomplete) {
      const setupReturnTo = currentIndex === null
        ? `${location.pathname}${location.search}${location.hash}`
        : location.state?.setupReturnTo
      navigate(setupPathForIndex(firstIncomplete), {
        replace: true,
        state: { setupReturnTo: safeReturnPath(setupReturnTo) },
      })
    }
  }, [location.hash, location.pathname, location.search, location.state, navigate, snapshot])

  useVisibilityRefresh({
    refresh,
    enabled: snapshot?.setup?.status !== 'complete',
    intervalMs: setupPollDelayMs(snapshot),
    refreshOnFocus: false,
  })

  if (loading) {
    return <CenteredShell testId="setup-gate-loading"><Spinner /></CenteredShell>
  }

  if (!snapshot?.setup) {
    return (
      <CenteredShell testId="setup-gate-error">
        <Card className="w-full max-w-md">
          <CardBody className="space-y-3 text-center">
            <h1 className="text-lg font-medium text-[var(--app-text)]">Couldn&apos;t load setup</h1>
            <p className="text-sm text-[var(--app-text-muted)]">
              Operations stays locked until the required setup checks can be confirmed.
            </p>
            <Button color="primary" onClick={refresh}>Retry</Button>
          </CardBody>
        </Card>
      </CenteredShell>
    )
  }

  if (snapshot.setup.status !== 'complete') {
    return (
      <SetupScreen
        snapshot={snapshot}
        onRefresh={refresh}
        onApplyTimezone={applyTimezone}
        onCreateVanityDomains={createVanityDomains}
        onDeleteVanityDomain={deleteVanityDomain}
        onConfigureMfaProvider={configureMfaProvider}
        onVerifyMfaProvider={verifyMfaProvider}
        onSkipMfaProvider={skipMfaProvider}
        onRequestApplianceDownload={requestApplianceDownload}
        onCreateApplianceEnrollmentCode={createApplianceEnrollmentCode}
        onStartApplianceBootstrap={startApplianceBootstrap}
        onApplyAiRunner={applyAiRunner}
        onFinishSetup={finishSetup}
        reconnecting={error}
      />
    )
  }

  if (setupIndexForPath(location.pathname) !== null) {
    return <CenteredShell testId="setup-gate-completing"><Spinner /></CenteredShell>
  }

  return children
}
